import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Modal,
  ModalSizes,
  Alert,
  AlertVariants,
  AlertAppearances,
  AlertOrientations,
  Button,
  ButtonVariants,
  ButtonSizes,
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
} from '@carboledger/ui';
import { FileUp } from '@carboledger/ui/icons';
import { useTranslations } from '@/hooks';
import { notification } from '@/lib/notifications';
import { openFile } from '@/lib/utils';
import { useAddAttachmentsMutation } from '@/lib/api/attachmentsApi';
import { FileAttachmentCard } from '../FileAttachmentCard';
import styles from './AddAttachmentModal.module.scss';

const MAX_ATTACHMENT_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_OTHER_DOCUMENTS = 5;
const MAX_OTHER_DOCUMENTS_TOTAL_BYTES = 25 * 1024 * 1024;
const ATTACHMENT_EXTENSIONS = Object.freeze(['.pdf', '.jpeg', '.jpg']);
const ATTACHMENT_ACCEPT = ATTACHMENT_EXTENSIONS.join(',');

// The picker's `accept` is only a hint — a drag or "All files" still lets anything through.
const isAcceptedType = (file) =>
  ATTACHMENT_EXTENSIONS.some((extension) => file.name.toLowerCase().endsWith(extension));

// One upload slot — label, the always-present trigger, its hint, then a card per selected file.
const AttachmentUploadField = ({
  label,
  hint,
  files,
  maxFiles,
  onFilesSelected,
  onRemoveFile,
  onView,
}) => {
  const t = useTranslations();
  const inputRef = useRef(null);

  const handleUploadClick = useCallback(() => inputRef.current?.click(), []);
  const handleInputChange = useCallback(
    (event) => {
      const selected = Array.from(event.target.files ?? []);
      if (selected.length > 0) onFilesSelected(selected);
      event.target.value = '';
    },
    [onFilesSelected],
  );

  return (
    <div className={styles.field}>
      <Typography
        variant={TypographyVariants.bodyXs}
        weight={TypographyWeights.semibold}
        color={TypographyColors.secondary}
      >
        {label}
      </Typography>
      <Button
        variant={ButtonVariants.secondary}
        size={ButtonSizes.sm}
        leftIcon={<FileUp />}
        onClick={handleUploadClick}
        // A single-file slot keeps its trigger, which is how a wrong pick is replaced.
        disabled={maxFiles > 1 && files.length >= maxFiles}
        fullWidth
      >
        {t('ATTACHMENT_UPLOAD')}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept={ATTACHMENT_ACCEPT}
        multiple={maxFiles > 1}
        hidden
        onChange={handleInputChange}
      />
      <Typography variant={TypographyVariants.bodyXs} color={TypographyColors.placeholder}>
        {hint}
      </Typography>
      {files.map((file) => (
        <FileAttachmentCard
          key={`${file.name}-${file.size}-${file.lastModified}`}
          file={file}
          onView={onView}
          onRemove={onRemoveFile}
          bordered={false}
          className={styles.fileCard}
        />
      ))}
    </div>
  );
};

/**
 * Props:
 *   open      whether the modal is shown
 *   onClose   close handler (also the footer's Cancel); called again once the save succeeds
 *   title     the modal header, e.g. "Add attachment: {contractId}"
 *   target    ATTACHMENT_TARGETS key — which record type the files are saved against
 *   module    the active module route segment
 *   entityId  the record's database id
 */
export const AddAttachmentModal = ({ open, onClose, title, target, module, entityId }) => {
  const t = useTranslations();
  const [billOfLading, setBillOfLading] = useState([]);
  const [invoice, setInvoice] = useState([]);
  const [otherDocuments, setOtherDocuments] = useState([]);
  const [addAttachments, { isLoading }] = useAddAttachmentsMutation();

  const resetFiles = useCallback(() => {
    setBillOfLading([]);
    setInvoice([]);
    setOtherDocuments([]);
  }, []);

  const handleClose = useCallback(() => {
    resetFiles();
    onClose();
  }, [resetFiles, onClose]);

  const handleBillOfLadingSelected = useCallback(
    (selected) => setBillOfLading(selected.slice(0, 1)),
    [],
  );
  const handleBillOfLadingRemoved = useCallback(() => setBillOfLading([]), []);
  const handleInvoiceSelected = useCallback((selected) => setInvoice(selected.slice(0, 1)), []);
  const handleInvoiceRemoved = useCallback(() => setInvoice([]), []);
  const handleOtherDocumentsSelected = useCallback(
    (selected) => setOtherDocuments((prev) => [...prev, ...selected].slice(0, MAX_OTHER_DOCUMENTS)),
    [],
  );
  const handleOtherDocumentRemoved = useCallback(
    (file) => setOtherDocuments((prev) => prev.filter((existing) => existing !== file)),
    [],
  );

  // Failures come from notifyApiErrors; the mutation's own notifyOnSuccess reports success.
  const handleAdd = useCallback(async () => {
    const errors = [...billOfLading, ...invoice, ...otherDocuments]
      .filter((file) => !isAcceptedType(file))
      .map((file) => t('ATTACHMENT_ERROR_FORMAT', { name: file.name }));
    if (billOfLading[0]?.size > MAX_ATTACHMENT_FILE_SIZE_BYTES) {
      errors.push(t('ATTACHMENT_ERROR_FILE_SIZE', { name: t('ATTACHMENT_BILL_OF_LADING') }));
    }
    if (invoice[0]?.size > MAX_ATTACHMENT_FILE_SIZE_BYTES) {
      errors.push(t('ATTACHMENT_ERROR_FILE_SIZE', { name: t('ATTACHMENT_INVOICE') }));
    }
    if (otherDocuments.some((file) => file.size > MAX_ATTACHMENT_FILE_SIZE_BYTES)) {
      errors.push(t('ATTACHMENT_ERROR_FILE_SIZE', { name: t('ATTACHMENT_OTHER_DOCUMENTS') }));
    }
    const otherDocumentsTotalBytes = otherDocuments.reduce((total, file) => total + file.size, 0);
    if (otherDocumentsTotalBytes > MAX_OTHER_DOCUMENTS_TOTAL_BYTES) {
      errors.push(t('ATTACHMENT_ERROR_TOTAL_SIZE'));
    }
    if (otherDocuments.length > MAX_OTHER_DOCUMENTS) {
      errors.push(t('ATTACHMENT_ERROR_MAX_FILES'));
    }

    if (errors.length > 0) {
      errors.forEach((message) => notification.error(message));
      return;
    }

    const result = await addAttachments({
      target,
      module,
      id: entityId,
      billOfLading,
      invoice,
      otherDocuments,
    });
    if (!result.error) handleClose();
  }, [
    billOfLading,
    invoice,
    otherDocuments,
    addAttachments,
    target,
    module,
    entityId,
    handleClose,
    t,
  ]);

  const hasFiles = billOfLading.length > 0 || invoice.length > 0 || otherDocuments.length > 0;

  const buttons = useMemo(
    () => [
      {
        label: t('COMMON_CANCEL'),
        variant: ButtonVariants.neutral,
        onClick: handleClose,
      },
      {
        label: t('ATTACHMENT_ADD'),
        variant: ButtonVariants.primary,
        onClick: handleAdd,
        disabled: !hasFiles || isLoading,
      },
    ],
    [t, handleClose, handleAdd, hasFiles, isLoading],
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size={ModalSizes.medium}
      title={title}
      buttons={buttons}
    >
      <div className={styles.body}>
        <Alert
          variant={AlertVariants.info}
          appearance={AlertAppearances.accent}
          orientation={AlertOrientations.horizontal}
        >
          {t('ATTACHMENT_INFO')}
        </Alert>
        <AttachmentUploadField
          label={t('ATTACHMENT_BILL_OF_LADING')}
          hint={t('ATTACHMENT_HINT_SINGLE')}
          files={billOfLading}
          maxFiles={1}
          onFilesSelected={handleBillOfLadingSelected}
          onRemoveFile={handleBillOfLadingRemoved}
          onView={openFile}
        />
        <AttachmentUploadField
          label={t('ATTACHMENT_INVOICE')}
          hint={t('ATTACHMENT_HINT_SINGLE')}
          files={invoice}
          maxFiles={1}
          onFilesSelected={handleInvoiceSelected}
          onRemoveFile={handleInvoiceRemoved}
          onView={openFile}
        />
        <AttachmentUploadField
          label={t('ATTACHMENT_OTHER_DOCUMENTS')}
          hint={t('ATTACHMENT_HINT_MULTIPLE')}
          files={otherDocuments}
          maxFiles={MAX_OTHER_DOCUMENTS}
          onFilesSelected={handleOtherDocumentsSelected}
          onRemoveFile={handleOtherDocumentRemoved}
          onView={openFile}
        />
      </div>
    </Modal>
  );
};
